import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Bell, Slack, MessageSquare, Smartphone, Plus, Trash2, TestTube,
  Settings, CheckCircle, XCircle, RefreshCw, Send, Zap, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Microsoft Teams icon component
const TeamsIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.5 6.5h-3v-2A1.5 1.5 0 0 0 15 3h-4.5A1.5 1.5 0 0 0 9 4.5v2H6A1.5 1.5 0 0 0 4.5 8v8A1.5 1.5 0 0 0 6 17.5h3v2A1.5 1.5 0 0 0 10.5 21H15a1.5 1.5 0 0 0 1.5-1.5v-2h3a1.5 1.5 0 0 0 1.5-1.5V8a1.5 1.5 0 0 0-1.5-1.5zM10.5 4.5H15v2h-4.5v-2zM15 19.5h-4.5v-8H15v8zm4.5-3.5h-3v-5.5a1.5 1.5 0 0 0-1.5-1.5h-4.5a1.5 1.5 0 0 0-1.5 1.5V16H6V8h13.5v8z"/>
  </svg>
);

export default function AlertIntegrationsPage() {
  const { toast } = useToast();
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('configs');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedType, setSelectedType] = useState('slack');
  const [testingId, setTestingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    webhook_url: '',
    channel: '',
    phone_numbers: '',
    severity_triggers: ['critical', 'high'],
    alert_types: ['hazard_detected', 'ppe_violation', 'unsafe_behavior']
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [configsRes, statsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/alert-integrations/configs`, { headers }),
        axios.get(`${API_URL}/api/alert-integrations/stats`, { headers }),
        axios.get(`${API_URL}/api/alert-integrations/logs?limit=20`, { headers })
      ]);

      setConfigs(configsRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load integrations", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddConfig = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      const config = {
        name: formData.name,
        enabled: true,
        severity_triggers: formData.severity_triggers,
        alert_types: formData.alert_types
      };

      if (selectedType === 'slack') {
        config.webhook_url = formData.webhook_url;
        if (formData.channel) config.channel = formData.channel;
      } else if (selectedType === 'teams') {
        config.webhook_url = formData.webhook_url;
      } else if (selectedType === 'sms') {
        config.phone_numbers = formData.phone_numbers.split(',').map(p => p.trim()).filter(p => p);
      }

      await axios.post(`${API_URL}/api/alert-integrations/configs`, {
        integration_type: selectedType,
        config
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ title: "Success", description: "Integration added successfully" });
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add integration", variant: "destructive" });
    }
  };

  const handleToggle = async (configId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/alert-integrations/configs/${configId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to toggle integration", variant: "destructive" });
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/api/alert-integrations/configs/${configId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Integration deleted" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete integration", variant: "destructive" });
    }
  };

  const handleTest = async (configId) => {
    setTestingId(configId);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/alert-integrations/test/${configId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast({ title: "Test Successful", description: response.data.message });
      } else {
        toast({ title: "Test Failed", description: response.data.message, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send test", variant: "destructive" });
    } finally {
      setTestingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      webhook_url: '',
      channel: '',
      phone_numbers: '',
      severity_triggers: ['critical', 'high'],
      alert_types: ['hazard_detected', 'ppe_violation', 'unsafe_behavior']
    });
  };

  const getIntegrationIcon = (type) => {
    switch (type) {
      case 'slack': return <Slack className="w-5 h-5 text-purple-500" />;
      case 'teams': return <TeamsIcon />;
      case 'sms': return <Smartphone className="w-5 h-5 text-green-500" />;
      default: return <Bell className="w-5 h-5" />;
    }
  };

  const severityOptions = ['critical', 'high', 'medium', 'low'];
  const alertTypeOptions = [
    { value: 'hazard_detected', label: 'Hazard Detected' },
    { value: 'ppe_violation', label: 'PPE Violation' },
    { value: 'unsafe_behavior', label: 'Unsafe Behavior' },
    { value: 'fall_risk', label: 'Fall Risk' },
    { value: 'equipment_issue', label: 'Equipment Issue' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-7 h-7 text-amber-500" />
            Alert Integrations
          </h1>
          <p className="text-slate-600 mt-1">
            Connect Slack, Microsoft Teams, and SMS for instant safety alerts
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          data-testid="add-integration-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Integration
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Integrations</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_configs}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Settings className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.enabled_configs}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Recent Triggers</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.recent_triggers}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <Send className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Success Rate</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.success_rate}%</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {['configs', 'logs'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab === 'configs' ? 'Integrations' : 'Activity Log'}
          </button>
        ))}
      </div>

      {/* Integrations List */}
      {activeTab === 'configs' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {configs.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No integrations configured yet</p>
                <p className="text-sm text-slate-400 mt-1">Add Slack, Teams, or SMS to receive instant alerts</p>
              </CardContent>
            </Card>
          ) : (
            configs.map((config) => (
              <Card key={config.id} className={`${!config.enabled ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getIntegrationIcon(config.integration_type)}
                      <div>
                        <CardTitle className="text-lg">{config.config.name}</CardTitle>
                        <CardDescription className="capitalize">
                          {config.integration_type}
                          {config.config.channel && ` • #${config.config.channel}`}
                        </CardDescription>
                      </div>
                    </div>
                    <Switch
                      checked={config.enabled}
                      onCheckedChange={() => handleToggle(config.id)}
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Severity Triggers */}
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Triggers on:</p>
                      <div className="flex flex-wrap gap-1">
                        {config.config.severity_triggers?.map((sev) => (
                          <Badge
                            key={sev}
                            variant="outline"
                            className={
                              sev === 'critical' ? 'border-red-300 text-red-700 bg-red-50' :
                              sev === 'high' ? 'border-orange-300 text-orange-700 bg-orange-50' :
                              sev === 'medium' ? 'border-yellow-300 text-yellow-700 bg-yellow-50' :
                              'border-green-300 text-green-700 bg-green-50'
                            }
                          >
                            {sev}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>Triggered {config.trigger_count || 0} times</span>
                      {config.last_triggered && (
                        <span>Last: {new Date(config.last_triggered).toLocaleDateString()}</span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTest(config.id)}
                        disabled={testingId === config.id}
                        data-testid={`test-integration-${config.id}`}
                      >
                        {testingId === config.id ? (
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <TestTube className="w-4 h-4 mr-1" />
                        )}
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(config.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Activity Log */}
      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest integration triggers and tests</CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No activity yet</p>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {log.success ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900 capitalize">
                          {log.action} - {log.integration_type}
                        </p>
                        <p className="text-sm text-slate-500">{log.message}</p>
                      </div>
                    </div>
                    <span className="text-sm text-slate-400">
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Integration Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add Integration</h2>

            {/* Integration Type Selector */}
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[
                { type: 'slack', icon: <Slack className="w-6 h-6" />, label: 'Slack', color: 'purple' },
                { type: 'teams', icon: <TeamsIcon />, label: 'Teams', color: 'blue' },
                { type: 'sms', icon: <Smartphone className="w-6 h-6" />, label: 'SMS', color: 'green' }
              ].map(({ type, icon, label, color }) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedType === type
                      ? `border-${color}-500 bg-${color}-50`
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`flex flex-col items-center gap-2 ${
                    selectedType === type ? `text-${color}-600` : 'text-slate-600'
                  }`}>
                    {icon}
                    <span className="font-medium">{label}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Configuration Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Safety Team Alerts"
                />
              </div>

              {(selectedType === 'slack' || selectedType === 'teams') && (
                <div>
                  <Label htmlFor="webhook_url">Webhook URL</Label>
                  <Input
                    id="webhook_url"
                    value={formData.webhook_url}
                    onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                    placeholder={selectedType === 'slack' 
                      ? "https://hooks.slack.com/services/..." 
                      : "https://outlook.office.com/webhook/..."
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedType === 'slack' 
                      ? "Create an incoming webhook in your Slack workspace settings"
                      : "Create an incoming webhook connector in your Teams channel"
                    }
                  </p>
                </div>
              )}

              {selectedType === 'slack' && (
                <div>
                  <Label htmlFor="channel">Channel Override (optional)</Label>
                  <Input
                    id="channel"
                    value={formData.channel}
                    onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
                    placeholder="#safety-alerts"
                  />
                </div>
              )}

              {selectedType === 'sms' && (
                <div>
                  <Label htmlFor="phone_numbers">Phone Numbers</Label>
                  <Input
                    id="phone_numbers"
                    value={formData.phone_numbers}
                    onChange={(e) => setFormData({ ...formData, phone_numbers: e.target.value })}
                    placeholder="+1234567890, +0987654321"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Comma-separated phone numbers with country code. Requires Twilio configuration.
                  </p>
                </div>
              )}

              {/* Severity Triggers */}
              <div>
                <Label>Trigger on Severity</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {severityOptions.map((sev) => (
                    <button
                      key={sev}
                      onClick={() => {
                        const triggers = formData.severity_triggers.includes(sev)
                          ? formData.severity_triggers.filter(s => s !== sev)
                          : [...formData.severity_triggers, sev];
                        setFormData({ ...formData, severity_triggers: triggers });
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        formData.severity_triggers.includes(sev)
                          ? sev === 'critical' ? 'bg-red-100 text-red-700 ring-2 ring-red-300' :
                            sev === 'high' ? 'bg-orange-100 text-orange-700 ring-2 ring-orange-300' :
                            sev === 'medium' ? 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-300' :
                            'bg-green-100 text-green-700 ring-2 ring-green-300'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {sev}
                    </button>
                  ))}
                </div>
              </div>

              {/* Alert Types */}
              <div>
                <Label>Alert Types</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {alertTypeOptions.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => {
                        const types = formData.alert_types.includes(value)
                          ? formData.alert_types.filter(t => t !== value)
                          : [...formData.alert_types, value];
                        setFormData({ ...formData, alert_types: types });
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        formData.alert_types.includes(value)
                          ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button
                onClick={handleAddConfig}
                disabled={!formData.name || (selectedType !== 'sms' && !formData.webhook_url) || (selectedType === 'sms' && !formData.phone_numbers)}
                className="bg-gradient-to-r from-blue-600 to-purple-600"
              >
                Add Integration
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
